import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUpdateConsultationMutation } from '@/core/services/consultation'
import { Consultation } from '@/core/models/consultation.model'
import { useIsFR } from '@/hooks/useIsFR'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'

const STORAGE_KEY = 'professional_preConsultationConsent_'
export const ETH03_ASSESSMENT_VERSION = 'ETH03-2026-06'

export type ConsentStep = 'dataReuse' | 'sensitiveData' | 'patientUnderstanding'

type UseProfessionalConsentDialogParams = {
  consultationId?: string
  professionalConsent?: Consultation['professionalConsent']
}

export function useProfessionalConsentDialog({ consultationId, professionalConsent }: UseProfessionalConsentDialogParams) {
  const navigate = useNavigate()
  const isFR = useIsFR()
  const authUser = useAppSelector(selectAuthUser)
  const [updateConsultation] = useUpdateConsultationMutation()

  const isDoctor = isStaff(authUser) && authUser.status === 'MEDECIN'
  // ETH consent dialog is France-only: hidden for every other language.
  const shouldShow = isFR && isDoctor

  const [isOpen, setIsOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState<ConsentStep>('dataReuse')
  const [stepChecked, setStepChecked] = useState(false)

  const isAlreadyConsented = useCallback(
    (id: string) => localStorage.getItem(`${STORAGE_KEY}${id}`) === 'true',
    []
  )

  const hasPreConsultationConsent = !!professionalConsent?.preConsultationConsent || !!(consultationId && isAlreadyConsented(consultationId))
  const hasUnderstandingAssessment = !!professionalConsent?.teleconsultationUnderstandingAssessed
  const requiredSteps = useMemo<ConsentStep[]>(
    () => [
      ...(!hasPreConsultationConsent ? (['dataReuse', 'sensitiveData'] as const) : []),
      ...(!hasUnderstandingAssessment ? (['patientUnderstanding'] as const) : []),
    ],
    [hasPreConsultationConsent, hasUnderstandingAssessment]
  )

  useEffect(() => {
    if (shouldShow && consultationId && requiredSteps.length > 0) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }, [shouldShow, consultationId, requiredSteps.length])

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(requiredSteps[0] ?? 'patientUnderstanding')
      setStepChecked(false)
    }
  }, [isOpen, requiredSteps])

  const handleContinue = useCallback(() => {
    const currentStepIndex = requiredSteps.indexOf(currentStep)
    const nextStep = requiredSteps[currentStepIndex + 1]

    if (nextStep) {
      setCurrentStep(nextStep)
      setStepChecked(false)
      return
    }

    if (consultationId) {
      localStorage.setItem(`${STORAGE_KEY}${consultationId}`, 'true')
      const now = new Date().toISOString()
      updateConsultation({
        id: consultationId,
        changes: {
          professionalConsent: {
            ...(professionalConsent ?? {}),
            dataReuseConsent: hasPreConsultationConsent ? professionalConsent?.dataReuseConsent : true,
            preConsultationConsent: true,
            consentDate: professionalConsent?.consentDate ?? now,
            teleconsultationUnderstandingAssessed: true,
            teleconsultationUnderstandingAssessedAt: professionalConsent?.teleconsultationUnderstandingAssessedAt ?? now,
            teleconsultationUnderstandingAssessmentVersion: professionalConsent?.teleconsultationUnderstandingAssessmentVersion ?? ETH03_ASSESSMENT_VERSION,
          },
        },
      })
      setIsOpen(false)
    }
  }, [consultationId, currentStep, hasPreConsultationConsent, professionalConsent, requiredSteps, updateConsultation])

  const handleBack = useCallback(() => {
    const currentStepIndex = requiredSteps.indexOf(currentStep)
    const previousStep = requiredSteps[currentStepIndex - 1]

    if (previousStep) {
      setCurrentStep(previousStep)
    }
    setStepChecked(false)
  }, [currentStep, requiredSteps])

  const canGoBack = requiredSteps.indexOf(currentStep) > 0
  const isCurrentStepFinal = requiredSteps.indexOf(currentStep) === requiredSteps.length - 1

  const handleCancel = useCallback(() => {
    setIsOpen(false)
    navigate('/')
  }, [navigate])

  return {
    isOpen,
    shouldShow,
    currentStep,
    stepChecked,
    setStepChecked,
    isContinueDisabled: !stepChecked,
    canGoBack,
    isCurrentStepFinal,
    handleContinue,
    handleBack,
    handleCancel,
  }
}
